import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiPlay, HiPause, HiVolumeUp, HiVolumeOff, HiHeart, HiOutlineHeart, HiDotsVertical, HiDownload, HiInformationCircle } from 'react-icons/hi';
import { FaSpotify, FaYoutube } from 'react-icons/fa';
import { IoIosSkipBackward, IoIosSkipForward } from 'react-icons/io';
import { MdRepeat, MdRepeatOne } from 'react-icons/md';
import { supabase } from '../supabaseClient';
import Navigation from './Navigation';

const MusicPlayer = () => {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [likedTracks, setLikedTracks] = useState(new Set());
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCredits, setShowCredits] = useState(false);
  const [showTrackMenu, setShowTrackMenu] = useState(null); // Track ID for which menu is open
  const [repeatMode, setRepeatMode] = useState('off'); // 'off', 'one'
  const audioRef = useRef(null);
  const menuRefs = useRef({}); // Store refs for each track menu
  const hasLoadedLikedTracks = useRef(false); // Ref to track if we've loaded liked tracks
  
  // Fetch tracks from Supabase
  useEffect(() => {
    const fetchTracks = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('music_tracks')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching tracks:', error);
        } else {
          setTracks(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTracks();
  }, []);
  
  // Load liked tracks from localStorage on component mount
  useEffect(() => {
    if (!hasLoadedLikedTracks.current) {
      const savedLikedTracks = localStorage.getItem('likedTracks');
      if (savedLikedTracks) {
        try {
          const parsedLikedTracks = JSON.parse(savedLikedTracks);
          if (Array.isArray(parsedLikedTracks)) {
            setLikedTracks(new Set(parsedLikedTracks));
          }
        } catch (error) {
          console.error('Error parsing liked tracks from localStorage:', error);
          localStorage.removeItem('likedTracks'); // Clear invalid data
        }
      }
      hasLoadedLikedTracks.current = true;
    }
  }, []);
  
  // Save liked tracks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('likedTracks', JSON.stringify([...likedTracks]));
  }, [likedTracks]);
  
  // Handle audio playback
  useEffect(() => {
    if (audioRef.current && tracks.length > 0) {
      if (isPlaying) {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentTrack, tracks]);
  
  // Sync video playback with audio
  useEffect(() => {
    // Get all video elements in the component
    const videoElements = document.querySelectorAll('video');
    
    if (isPlaying) {
      // Play all videos when audio plays
      videoElements.forEach(video => {
        if (video) {
          video.play().catch(error => {
            console.log('Error playing video:', error);
          });
        }
      });
    } else {
      // Pause all videos when audio pauses
      videoElements.forEach(video => {
        if (video) {
          video.pause();
        }
      });
    }
  }, [isPlaying]);
  
  // Update progress bar
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const updateProgress = () => {
      const currentTime = audio.currentTime;
      const duration = audio.duration || 1;
      setProgress((currentTime / duration) * 100);
    };
    
    audio.addEventListener('timeupdate', updateProgress);
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
    };
  }, [tracks]);
  
  // Handle track end
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const handleEnded = () => {
      handleNext();
    };
    
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentTrack, tracks, repeatMode]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside of any menu
      const isClickOutside = Object.values(menuRefs.current).every(
        ref => !ref || !ref.contains(event.target)
      );
      
      if (isClickOutside) {
        setShowTrackMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handlePrevious = () => {
    // If more than 3 seconds have passed, restart current track
    if (audioRef.current && audioRef.current.currentTime > 3) {
      setProgress(0);
      audioRef.current.currentTime = 0;
      setIsPlaying(true);
    } else {
      // Otherwise go to previous track
      setCurrentTrack(prev => (prev === 0 ? tracks.length - 1 : prev - 1));
      setProgress(0);
      setIsPlaying(true);
    }
  };

  const handleNext = () => {
    if (repeatMode === 'one') {
      // Repeat current track
      setProgress(0);
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(true);
    } else {
      // No repeat - go to next track or stop at end
      if (currentTrack < tracks.length - 1) {
        setCurrentTrack(prev => prev + 1);
        setProgress(0);
        setIsPlaying(true);
      } else {
        // At the end of playlist
        setIsPlaying(false);
        setProgress(0);
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      }
    }
  };

  const toggleLike = async (trackId) => {
    const newLikedTracks = new Set(likedTracks);
    const isCurrentlyLiked = newLikedTracks.has(trackId);
    
    if (isCurrentlyLiked) {
      newLikedTracks.delete(trackId);
    } else {
      newLikedTracks.add(trackId);
    }
    
    setLikedTracks(newLikedTracks);
    
    // Update the likes count in the database
    try {
      // Find the track in our local state
      const trackIndex = tracks.findIndex(track => track.id === trackId);
      if (trackIndex !== -1) {
        const track = tracks[trackIndex];
        const newLikes = isCurrentlyLiked ? Math.max(0, (track.likes || 0) - 1) : (track.likes || 0) + 1;
        
        // Update the database
        const { error } = await supabase
          .from('music_tracks')
          .update({ likes: newLikes })
          .eq('id', trackId);
        
        if (error) {
          console.error('Error updating likes:', error);
        } else {
          // Update local state
          const updatedTracks = [...tracks];
          updatedTracks[trackIndex] = { ...track, likes: newLikes };
          setTracks(updatedTracks);
        }
      }
    } catch (error) {
      console.error('Error updating likes:', error);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  const handleProgressChange = (e) => {
    const newProgress = parseFloat(e.target.value);
    setProgress(newProgress);
    if (audioRef.current && tracks.length > 0) {
      const duration = audioRef.current.duration || 1;
      audioRef.current.currentTime = (newProgress / 100) * duration;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const openCredits = () => {
    setShowCredits(true);
    setShowTrackMenu(null); // Close the menu
  };

  const closeCredits = () => {
    setShowCredits(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen py-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-green mx-auto mb-4"></div>
          <p>Loading music tracks...</p>
        </div>
      </div>
    );
  }

  if (tracks.length === 0) {
    return (
      <div className="min-h-screen py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-display-2 font-space-grotesk font-bold mb-4">
              <span className="gradient-text-green">My Music</span> Palace
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
              Here you'll find a showcase of my favorite tracks, unreleased songs, some good old leaks and sometimes some special music.
            </p>
            <div className="glass-morphism-strong rounded-3xl p-12 max-w-2xl mx-auto">
              <div className="text-center">
                <HiOutlineHeart className="h-16 w-16 text-gray-500 mx-auto mb-4" />
                <h3 className="text-2xl font-space-grotesk font-bold mb-2">No tracks available</h3>
                <p className="text-gray-400 mb-6">
                  Check back later for new music releases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentTrackData = tracks[currentTrack];
  const currentTime = audioRef.current ? audioRef.current.currentTime : 0;
  const duration = audioRef.current ? audioRef.current.duration : 0;

  return (
    <div className="min-h-screen py-20">
      <Navigation />
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={currentTrackData?.file_url}
        volume={volume}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-display-2 font-space-grotesk font-bold mb-4">
            <span className="gradient-text-green">My Music</span> Palace
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Here you'll find a showcase of my favorite tracks, unreleased songs, some good old leaks and sometimes some special music.
          </p>
        </div>

        {/* Main Player Section */}
        <div className="glass-morphism-strong rounded-3xl p-8 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Album Art */}
            <div className="lg:col-span-1 flex justify-center">
              <motion.div 
                className="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden shadow-2xl"
                whileHover={{ scale: 1.03 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Link to={`/music/${currentTrackData.id}`}>
                  {currentTrackData.cover_url && (currentTrackData.cover_url.endsWith('.mp4') || currentTrackData.cover_url.endsWith('.gif')) ? (
                    <video 
                      src={currentTrackData.cover_url} 
                      alt={currentTrackData.title}
                      className="w-full h-full object-cover"
                      ref={(video) => {
                        if (video) {
                          // Sync video with audio playback
                          if (isPlaying) {
                            video.play().catch(e => console.log("Video play error:", e));
                          } else {
                            video.pause();
                          }
                        }
                      }}
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img 
                      src={currentTrackData.cover_url} 
                      alt={currentTrackData.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </Link>
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              </motion.div>
            </div>

            {/* Track Info & Controls */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-3xl md:text-4xl font-space-grotesk font-bold mb-2">
                      <Link to={`/music/${currentTrackData.id}`} className="hover:text-accent-green">
                        {currentTrackData.title}
                      </Link>
                    </h2>
                    
                    {/* Artist Name */}
                    <p className="text-xl text-gray-300 mb-4">
                      {currentTrackData.artist || 'Unknown Artist'}
                    </p>
                    
                    <p className="text-lg text-gray-400">{currentTrackData.album || 'Single'}</p>
                  </div>
                  
                  {/* Three dots menu for main player */}
                  <div className="relative" ref={el => menuRefs.current[`main-${currentTrackData.id}`] = el}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTrackMenu(showTrackMenu === `main-${currentTrackData.id}` ? null : `main-${currentTrackData.id}`);
                      }}
                      className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white"
                    >
                      <HiDotsVertical className="h-5 w-5" />
                    </button>
                    
                    <AnimatePresence>
                      {showTrackMenu === `main-${currentTrackData.id}` && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute right-0 mt-2 w-48 bg-secondary/70 backdrop-blur rounded-lg shadow-lg py-2 z-10 border border-white/20"
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openCredits();
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                          >
                            <HiInformationCircle className="mr-2" />
                            Credits
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                // Get the current track data
                                const trackUrl = `${window.location.origin}/music/${currentTrackData.id}`;
                                
                                if (navigator.share) {
                                  await navigator.share({
                                    title: currentTrackData.title,
                                    text: `Check out this track: ${currentTrackData.title} by ${currentTrackData.artist || 'Unknown Artist'}`,
                                    url: trackUrl
                                  });
                                } else {
                                  // Fallback: copy to clipboard
                                  await navigator.clipboard.writeText(trackUrl);
                                  // Show a toast or alert that the link was copied
                                  alert('Link copied to clipboard!');
                                }
                              } catch (error) {
                                console.error('Error sharing:', error);
                                // Fallback: copy to clipboard
                                try {
                                  const trackUrl = `${window.location.origin}/music/${currentTrackData.id}`;
                                  await navigator.clipboard.writeText(trackUrl);
                                  alert('Link copied to clipboard!');
                                } catch (copyError) {
                                  console.error('Error copying to clipboard:', copyError);
                                  // Last fallback: show the URL
                                  const trackUrl = `${window.location.origin}/music/${currentTrackData.id}`;
                                  prompt('Copy this link:', trackUrl);
                                }
                              }
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                            Share
                          </button>
                          {currentTrackData.spotify_url && (
                            <a
                              href={currentTrackData.spotify_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                            >
                              <FaSpotify className="mr-2 text-green-500" />
                              Listen on Spotify
                            </a>
                          )}
                          {currentTrackData.youtube_url && (
                            <a
                              href={currentTrackData.youtube_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                            >
                              <FaYoutube className="mr-2 text-red-500" />
                              Watch on YouTube
                            </a>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLike(currentTrackData.id);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                          >
                            {likedTracks.has(currentTrackData.id) ? (
                              <HiHeart className="mr-2 text-red-500" />
                            ) : (
                              <HiOutlineHeart className="mr-2" />
                            )}
                            {likedTracks.has(currentTrackData.id) ? 'Unlike' : 'Like'}
                            {currentTrackData.likes > 0 && ` (${currentTrackData.likes})`}
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              // Download the track
                              if (currentTrackData.file_url) {
                                try {
                                  // Fetch the file as a blob
                                  const response = await fetch(currentTrackData.file_url);
                                  const blob = await response.blob();
                                  
                                  // Create filename with song name and artist
                                  const songName = currentTrackData.title || 'track';
                                  const artistName = currentTrackData.artist || 'unknown-artist';
                                  // Replace spaces with hyphens and remove special characters except hyphens
                                  const cleanSongName = songName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
                                  const cleanArtistName = artistName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
                                  const filename = `${cleanSongName}_${cleanArtistName}.mp3`;
                                  
                                  // Create a blob URL and trigger download
                                  const blobUrl = window.URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = blobUrl;
                                  link.download = filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                  
                                  // Clean up the blob URL
                                  setTimeout(() => {
                                    window.URL.revokeObjectURL(blobUrl);
                                  }, 100);
                                } catch (error) {
                                  console.error('Download failed:', error);
                                  // Fallback to opening in new tab
                                  window.open(currentTrackData.file_url, '_blank');
                                }
                              }
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                          >
                            <HiDownload className="mr-2" />
                            Download
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
                <div className="relative h-2 bg-gray-700 rounded-lg overflow-hidden">
                  <div 
                    className="absolute top-0 left-0 h-full bg-accent-green"
                    style={{ width: `${progress}%` }}
                  ></div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={progress}
                    onChange={handleProgressChange}
                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-col items-center mb-8">
                <div className="flex items-center space-x-8 mb-6">
                  <button 
                    onClick={handlePrevious}
                    className="text-gray-300 hover:text-white transition-colors focus:outline-none"
                    aria-label="Previous track"
                  >
                    <IoIosSkipBackward className="h-8 w-8" />
                  </button>
                  
                  <button 
                    onClick={togglePlay}
                    className="w-16 h-16 rounded-full bg-accent-green flex items-center justify-center hover:bg-accent-green/80 transition-colors focus:outline-none"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <HiPause className="h-8 w-8 text-black" />
                    ) : (
                      <HiPlay className="h-8 w-8 text-black" />
                    )}
                  </button>
                  
                  <button 
                    onClick={handleNext}
                    className="text-gray-300 hover:text-white transition-colors focus:outline-none"
                    aria-label="Next track"
                  >
                    <IoIosSkipForward className="h-8 w-8" />
                  </button>
                  
                  {/* Repeat Button */}
                  <div className="relative">
                    <button 
                      onClick={() => {
                        if (repeatMode === 'off') {
                          setRepeatMode('one');
                        } else {
                          setRepeatMode('off');
                        }
                      }}
                      className={`text-gray-300 hover:text-white transition-colors focus:outline-none ${repeatMode !== 'off' ? 'text-accent-green' : ''}`}
                      aria-label={`Repeat mode: ${repeatMode}`}
                    >
                      {repeatMode === 'one' ? (
                        <MdRepeatOne className="h-6 w-6" />
                      ) : (
                        <MdRepeat className="h-6 w-6" />
                      )}
                      {repeatMode === 'one' && (
                        <span className="absolute -top-1 -right-1 bg-accent-green text-black text-xs rounded-full h-4 w-4 flex items-center justify-center">1</span>
                      )}
                    </button>
                  </div>
                </div>

                {/* Volume Control */}
                <div className="flex items-center space-x-4 w-full max-w-xs">
                  <button 
                    onClick={toggleMute}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {isMuted || volume === 0 ? (
                      <HiVolumeOff className="h-5 w-5" />
                    ) : (
                      <HiVolumeUp className="h-5 w-5" />
                    )}
                  </button>
                  <div className="relative h-2 bg-gray-700 rounded-lg overflow-hidden flex-1">
                    <div 
                      className="absolute top-0 left-0 h-full bg-accent-green"
                      style={{ width: `${isMuted ? 0 : volume * 100}%` }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                    />
                  </div>
                </div>
              </div>

              {/* Like Button and External Links */}
              <div className="flex justify-center items-center space-x-4">
                <button 
                  onClick={() => toggleLike(currentTrackData.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
                    likedTracks.has(currentTrackData.id) 
                      ? 'text-red-500 bg-red-500/10' 
                      : 'text-gray-300 hover:text-red-500 hover:bg-red-500/10'
                  } transition-colors`}
                >
                  {likedTracks.has(currentTrackData.id) ? (
                    <HiHeart className="h-5 w-5" />
                  ) : (
                    <HiOutlineHeart className="h-5 w-5" />
                  )}
                  <span>Like</span>
                  {currentTrackData.likes > 0 && ` (${currentTrackData.likes})`}
                </button>
                
                {/* External Links */}
                <div className="flex space-x-3">
                  {currentTrackData.spotify_url && (
                    <a 
                      href={currentTrackData.spotify_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-500 hover:text-green-400 transition-colors"
                    >
                      <FaSpotify className="h-6 w-6" />
                    </a>
                  )}
                  {currentTrackData.youtube_url && (
                    <a 
                      href={currentTrackData.youtube_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-red-500 hover:text-red-400 transition-colors"
                    >
                      <FaYoutube className="h-6 w-6" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Playlist */}
        <div className="glass-morphism-strong rounded-3xl p-8">
          <h3 className="text-2xl font-space-grotesk font-bold mb-6">My Tracks</h3>
          <div className="space-y-4">
            {tracks.map((track, index) => (
              <motion.div
                key={track.id}
                className={`flex items-center p-4 rounded-xl cursor-pointer transition-all ${
                  index === currentTrack 
                    ? 'bg-accent-green/20 border border-accent-green/30' 
                    : 'hover:bg-white/5'
                }`}
                onClick={() => {
                  setCurrentTrack(index);
                  setProgress(0);
                  setIsPlaying(true); // Auto-play when selecting a track
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link to={`/music/${track.id}`} className="w-12 h-12 rounded-lg overflow-hidden mr-4">
                  {track.cover_url && (track.cover_url.endsWith('.mp4') || track.cover_url.endsWith('.gif')) ? (
                    <video 
                      src={track.cover_url} 
                      alt={track.title}
                      className="w-full h-full object-cover"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                  ) : (
                    <img 
                      src={track.cover_url} 
                      alt={track.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/music/${track.id}`} className="font-medium truncate hover:text-accent-green">
                    {track.title}
                  </Link>
                  <p className="text-sm text-gray-400 truncate">
                    {track.artist || 'Unknown Artist'}
                  </p>
                </div>
                <div className="text-sm text-gray-400 mr-4 hidden md:block">{track.duration}</div>
                
                {/* Three dots menu */}
                <div 
                  className="relative" 
                  ref={el => menuRefs.current[track.id] = el}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowTrackMenu(showTrackMenu === track.id ? null : track.id);
                    }}
                    className="p-2 rounded-full hover:bg-white/10 text-gray-400 hover:text-white"
                  >
                    <HiDotsVertical className="h-5 w-5" />
                  </button>
                  
                  <AnimatePresence>
                    {showTrackMenu === track.id && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-2 w-48 bg-secondary/70 backdrop-blur rounded-lg shadow-lg py-2 z-10 border border-white/20"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCredits();
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                        >
                          <HiInformationCircle className="mr-2" />
                          Credits
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              // Get the current track data
                              const trackUrl = `${window.location.origin}/music/${track.id}`;
                              
                              if (navigator.share) {
                                await navigator.share({
                                  title: track.title,
                                  text: `Check out this track: ${track.title} by ${track.artist || 'Unknown Artist'}`,
                                  url: trackUrl
                                });
                              } else {
                                // Fallback: copy to clipboard
                                await navigator.clipboard.writeText(trackUrl);
                                // Show a toast or alert that the link was copied
                                alert('Link copied to clipboard!');
                              }
                            } catch (error) {
                              console.error('Error sharing:', error);
                              // Fallback: copy to clipboard
                              try {
                                const trackUrl = `${window.location.origin}/music/${track.id}`;
                                await navigator.clipboard.writeText(trackUrl);
                                alert('Link copied to clipboard!');
                              } catch (copyError) {
                                console.error('Error copying to clipboard:', copyError);
                                // Last fallback: show the URL
                                const trackUrl = `${window.location.origin}/music/${track.id}`;
                                prompt('Copy this link:', trackUrl);
                              }
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Share
                        </button>
                        {track.spotify_url && (
                          <a
                            href={track.spotify_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                          >
                            <FaSpotify className="mr-2 text-green-500" />
                            Listen on Spotify
                          </a>
                        )}
                        {track.youtube_url && (
                          <a
                            href={track.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                          >
                            <FaYoutube className="mr-2 text-red-500" />
                            Watch on YouTube
                          </a>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleLike(track.id);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                        >
                          {likedTracks.has(track.id) ? (
                            <HiHeart className="mr-2 text-red-500" />
                          ) : (
                            <HiOutlineHeart className="mr-2" />
                          )}
                          {likedTracks.has(track.id) ? 'Unlike' : 'Like'}
                          {track.likes > 0 && ` (${track.likes})`}
                        </button>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            // Download the track
                            if (track.file_url) {
                              try {
                                // Fetch the file as a blob
                                const response = await fetch(track.file_url);
                                const blob = await response.blob();
                                
                                // Create filename with song name and artist
                                const songName = track.title || 'track';
                                const artistName = track.artist || 'unknown-artist';
                                // Replace spaces with hyphens and remove special characters except hyphens
                                const cleanSongName = songName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
                                const cleanArtistName = artistName.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
                                const filename = `${cleanSongName}_${cleanArtistName}.mp3`;
                                
                                // Create a blob URL and trigger download
                                const blobUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.download = filename;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                
                                // Clean up the blob URL
                                setTimeout(() => {
                                  window.URL.revokeObjectURL(blobUrl);
                                }, 100);
                              } catch (error) {
                                console.error('Download failed:', error);
                                // Fallback to opening in new tab
                                window.open(track.file_url, '_blank');
                              }
                            }
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center"
                        >
                          <HiDownload className="mr-2" />
                          Download
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Credits Modal */}
      <AnimatePresence>
        {showCredits && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4"
            onClick={closeCredits}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-morphism-strong rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-space-grotesk font-bold">Track Credits</h3>
                <button
                  onClick={closeCredits}
                  className="text-gray-400 hover:text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {currentTrackData.roles && currentTrackData.roles.length > 0 ? (
                <div className="space-y-3">
                  {currentTrackData.roles
                    .filter(role => role.role && role.name) // Filter out empty roles
                    .map((role, index) => (
                      <div key={index} className="flex justify-between items-center py-2 border-b border-white/10">
                        <span className="font-medium">{role.role}</span>
                        <span className="text-gray-300">{role.name}</span>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No credits available for this track</p>
              )}
              
              <div className="mt-6 pt-4 border-t border-white/10">
                <div className="flex justify-between text-sm text-gray-400">
                  <span>Track</span>
                  <span>{currentTrackData.title}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-400 mt-1">
                  <span>Artist</span>
                  <span>{currentTrackData.artist || 'Unknown Artist'}</span>
                </div>
                {currentTrackData.album && (
                  <div className="flex justify-between text-sm text-gray-400 mt-1">
                    <span>Album</span>
                    <span>{currentTrackData.album}</span>
                  </div>
                )}
                
                {/* Verified Badges */}
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-xs text-gray-400 mb-2">Verified By:</p>
                  <div className="flex items-center space-x-6">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center mr-2">
                        <FaSpotify className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs text-gray-300">Spotify</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded-full overflow-hidden mr-2">
                        <img 
                          src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT7vQbpYxdR5FeuXKSepkKwaW8QtK_cuC-Pig&s" 
                          alt="Genius" 
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span className="text-xs text-gray-300">Genius</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MusicPlayer;